import React, { useMemo, useState, useEffect } from 'react';
import { Container, Card, Form, Table, Badge, Spinner, Alert, Pagination, Button } from 'react-bootstrap';
import { FiFilter } from 'react-icons/fi';
import { auditLogAPI } from '../../utils/api';
import '../styles/AuditLogs.css';
import * as XLSX from 'xlsx';
/* eslint-disable react-hooks/exhaustive-deps */

const stringify = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const humanizeChangedData = (changedData) => {
  if (!changedData) return '-';
  const cd = typeof changedData === 'string'
    ? (() => { try { return JSON.parse(changedData); } catch { return { _raw: changedData }; } })()
    : changedData;

  if (!cd || typeof cd !== 'object') return String(cd);

  const pairs = [];
  const keys = Object.keys(cd);
  for (const k of keys) {
    const v = cd[k];
    if (v === null || v === undefined || v === '') continue;
    if (typeof v === 'object') continue; // keep it readable; don't dump nested JSON
    pairs.push(`${k}: ${String(v)}`);
  }

  // If everything is nested, at least show the asset id if present.
  if (!pairs.length) {
    const assetId = getAssetIdFromChangedData(cd);
    if (assetId) return `assetId: ${assetId}`;
    return 'Details available';
  }

  return pairs.join(' | ');
};

const getLogTimestamp = (log) => {
  const raw = log?.timestamp || log?.createdAt || log?.updatedAt;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getAssetIdFromChangedData = (changedData) => {
  if (!changedData) return '';
  const cd = typeof changedData === 'string'
    ? (() => { try { return JSON.parse(changedData); } catch { return {}; } })()
    : changedData;

  return (
    cd.assetId
    || cd.asset_id
    || cd?.asset?.assetId
    || cd?.asset?.asset_id
    || ''
  );
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [moduleFilter, setModuleFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const modules = useMemo(() => {
    const unique = Array.from(new Set((logs || []).map((l) => l?.module).filter(Boolean)))
      .sort((a, b) => String(a).localeCompare(String(b)));
    return ['all', ...unique];
  }, [logs]);

  const actions = useMemo(() => {
    const unique = Array.from(new Set((logs || []).map((l) => l?.action).filter(Boolean)))
      .sort((a, b) => String(a).localeCompare(String(b)));
    return ['all', ...unique];
  }, [logs]);

  const statuses = ['all', 'success', 'failed', 'pending'];

  /**
   * Fetch audit logs
   */
  useEffect(() => {
    fetchAuditLogs();
  }, []);

  /**
   * Apply filters
   */
  useEffect(() => {
    applyFilters();
  }, [logs, moduleFilter, actionFilter, statusFilter, dateFrom, dateTo, searchTerm]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await auditLogAPI.getAllAuditLogs(1, 100);
      if (response.success && response.data) {
        setLogs(response.data);
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = logs;

    // Module filter
    if (moduleFilter !== 'all') {
      filtered = filtered.filter(log => log.module === moduleFilter);
    }

    // Action filter
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action === actionFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(log => log.status === statusFilter);
    }

    // Date range filter (inclusive)
    if (dateFrom || dateTo) {
      const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
      const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;

      filtered = filtered.filter((log) => {
        const ts = getLogTimestamp(log);
        if (!ts) return false;
        if (from && ts < from) return false;
        if (to && ts > to) return false;
        return true;
      });
    }

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((log) =>
        stringify(log?.description).toLowerCase().includes(term) ||
        stringify(log?.module).toLowerCase().includes(term) ||
        stringify(log?.action).toLowerCase().includes(term) ||
        stringify(log?.user_email).toLowerCase().includes(term) ||
        stringify(log?.user_name).toLowerCase().includes(term) ||
        stringify(getAssetIdFromChangedData(log?.changedData)).toLowerCase().includes(term) ||
        stringify(log?.changedData).toLowerCase().includes(term)
      );
    }

    setFilteredLogs(filtered);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLogs = filteredLogs.slice(startIndex, startIndex + itemsPerPage);

  const downloadXlsx = async () => {
    const rows = (filteredLogs || []).map((log) => {
      const timestamp = getLogTimestamp(log);
      return {
        Timestamp: timestamp ? timestamp.toISOString() : '',
        Module: log?.module || '',
        Action: log?.action || '',
        'Asset ID': getAssetIdFromChangedData(log?.changedData) || '',
        Description: log?.description || '',
        Details: humanizeChangedData(log?.changedData),
                        User: log?.user_email || log?.user_name || String(log?.userId ?? ''),
        Status: log?.status || ''
      };
    });

    // Log export action
    try {
      await auditLogAPI.logClientEvent({
        module: 'audit_logs',
        action: 'download_xlsx',
        description: 'Downloaded audit logs (xlsx)',
        changedData: {
          filters: {
            module: moduleFilter,
            action: actionFilter,
            status: statusFilter,
            from: dateFrom || null,
            to: dateTo || null,
            search: searchTerm || null
          },
          count: (filteredLogs || []).length
        }
      });
    } catch {
      // ignore audit logging failure
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit Logs');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `audit-logs-${stamp}.xlsx`);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading audit logs...</p>
      </div>
    );
  }

  return (
    <Container fluid className="audit-logs">
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Filters */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">
              <FiFilter className="me-2" />
              Filters
            </h5>
            <Button variant="outline-success" size="sm" onClick={downloadXlsx} disabled={!filteredLogs.length}>
              Download Excel
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="audit-filters-bar">
            <Form.Group className="audit-filter-item">
              <Form.Label className="filter-label">Module</Form.Label>
              <Form.Select
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
                className="filter-select"
              >
                {modules.map(m => (
                  <option key={m} value={m}>
                    {m.charAt(0).toUpperCase() + m.slice(1)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="audit-filter-item">
              <Form.Label className="filter-label">Action</Form.Label>
              <Form.Select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="filter-select"
              >
                {actions.map(a => (
                  <option key={a} value={a}>
                    {a.charAt(0).toUpperCase() + a.slice(1)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="audit-filter-item">
              <Form.Label className="filter-label">Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                {statuses.map(s => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="audit-filter-item">
              <Form.Label className="filter-label">From</Form.Label>
              <Form.Control
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="filter-input"
              />
            </Form.Group>

            <Form.Group className="audit-filter-item">
              <Form.Label className="filter-label">To</Form.Label>
              <Form.Control
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="filter-input"
              />
            </Form.Group>

            <Form.Group className="audit-filter-item audit-filter-search">
              <Form.Label className="filter-label">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="filter-input"
              />
            </Form.Group>
          </div>
        </Card.Body>
      </Card>

      {/* Audit Logs Table */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {paginatedLogs.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No audit logs found
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="audit-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Module</th>
                      <th>Action</th>
                      <th>Asset ID</th>
                      <th>Description</th>
                      <th>Details</th>
                      <th>User</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedLogs.map((log, index) => (
                      <tr key={index} className="log-row">
                        <td>
                          <small>{(getLogTimestamp(log) || new Date()).toLocaleString()}</small>
                        </td>
                        <td>
                          <Badge bg="light" text="dark">
                            {log.module}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg="info">
                            {log.action}
                          </Badge>
                        </td>
                        <td>
                          <small className="text-monospace">{getAssetIdFromChangedData(log?.changedData) || '-'}</small>
                        </td>
                        <td>
                          <small>{log.description}</small>
                        </td>
                        <td>
                          <small className="text-muted">{humanizeChangedData(log.changedData)}</small>
                        </td>
                        <td>
                          <small>{log.user_email || log.user_name || log.userId || '-'}</small>
                        </td>
                        <td>
                          <Badge bg={log.status === 'success' ? 'success' : (log.status === 'pending' ? 'warning' : 'danger')}>
                            {log.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-wrapper">
                  <Pagination className="justify-content-center mb-0">
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    />

                    {[...Array(Math.min(totalPages, 5))].map((_, index) => (
                      <Pagination.Item
                        key={index + 1}
                        active={index + 1 === currentPage}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </Pagination.Item>
                    ))}

                    {totalPages > 5 && <Pagination.Ellipsis disabled />}

                    <Pagination.Next
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AuditLogs;
